export type GuidePageKey =
  | "login"
  | "initial-setup"
  | "accept-invitation"
  | "change-password"
  | "dashboard"
  | "follow-ups"
  | "whatsapp"
  | "customers"
  | "orders"
  | "production"
  | "dispatch"
  | "finance"
  | "marketing"
  | "intelligence"
  | "calendar"
  | "admin"
  | "profile";

export type GuideContent = {
  title: string;
  purpose: string;
  action: string;
  mistake: string;
  successRule: string;
  checklist?: string[];
};

export const guideContent: Record<GuidePageKey, GuideContent> = {
  login: {
    title: "Secure Staff Entry",
    purpose: "Use your individual staff account so every operational action has a real owner.",
    action: "Sign in with your assigned email and the password you created through setup or invitation.",
    mistake: "Do not share one login across departments; audit trails depend on the real user.",
    successRule: "Every action in the Company OS should be traceable to the person who performed it.",
    checklist: ["Use your own account", "Use a strong password", "Never share credentials"],
  },
  "initial-setup": {
    title: "First Owner Setup",
    purpose: "Create the first accountable Owner only when the database has zero users.",
    action: "Register the real owner identity with a strong password and then invite staff from Admin.",
    mistake: "Do not create shared admin accounts or default launch passwords.",
    successRule: "The first account should be a real owner who controls staff provisioning and permissions.",
    checklist: ["Use real owner identity", "Use strong password", "Invite staff individually"],
  },
  "accept-invitation": {
    title: "Invitation-Based Onboarding",
    purpose: "Give every teammate a private password and individual audit trail.",
    action: "Use the one-time invitation link, set a strong password, and sign in with your own email.",
    mistake: "Do not forward invitation links or reuse another teammate's account.",
    successRule: "Every staff user should enter through an individual invitation and never through a shared password.",
  },
  "change-password": {
    title: "Set Your Permanent Password",
    purpose: "Keep your individual staff account secure before using operational data.",
    action: "Choose a password that only you know and continue to the Command Center.",
    mistake: "Do not reuse old or shared passwords.",
    successRule: "A clean password reset keeps the operating system accountable and secure.",
  },
  dashboard: {
    title: "Morning Control Room",
    purpose: "Start every day with the risks that can hurt customers first.",
    action: "Clear overdue follow-ups, waiting WhatsApp conversations, production risks, dispatch risk, and pending payments.",
    mistake: "Do not jump to analytics before operational exceptions are under control.",
    successRule: "The day is healthy when urgent customer, production, dispatch, and payment queues are visible and owned.",
    checklist: ["Overdue follow-ups checked", "Waiting WhatsApp owned", "Production risks flagged", "Dispatch and payments reviewed"],
  },
  "follow-ups": {
    title: "Sales Discipline",
    purpose: "Protect every inquiry from being forgotten after the first conversation.",
    action: "Work overdue tasks first and complete a task only after the customer has actually been contacted.",
    mistake: "Do not mark a follow-up complete because a reminder was seen; mark it complete only after human action.",
    successRule: "Every open inquiry has a next action, owner, and due time.",
  },
  whatsapp: {
    title: "Human-Owned WhatsApp Hub",
    purpose: "Review customer context and conversation history before replying.",
    action: "Assign ownership, read context, reply manually when appropriate, and keep the relationship manager accountable.",
    mistake: "Never mark a message handled without real human review.",
    successRule: "Inbound messages are captured; customer-facing sends remain deliberate human actions.",
  },
  customers: {
    title: "Customer Memory",
    purpose: "Build the repeat-business memory layer for preferences, occasions, and service context.",
    action: "Search customers, review history, and record details that help the next order feel personal.",
    mistake: "Do not leave important preference or occasion details inside WhatsApp only.",
    successRule: "A repeat customer should feel remembered, not re-interviewed.",
  },
  orders: {
    title: "Sales and Order Desk",
    purpose: "Convert inquiries into clean, production-ready orders.",
    action: "Confirm design, delivery timing, payment, allergen notes, and special instructions before final confirmation.",
    mistake: "Do not push an unclear design or unpaid confirmation into production.",
    successRule: "Every confirmed order should be clear enough for production and dispatch to execute without guessing.",
  },
  production: {
    title: "Production Truth Board",
    purpose: "Keep cake status aligned with physical reality in the kitchen.",
    action: "Update status only when physically true and raise flags immediately when design, ingredient, or timing is unclear.",
    mistake: "Do not move an order to ready before final check and packaging readiness.",
    successRule: "Production status should match what is actually happening, not what we hope is happening.",
  },
  dispatch: {
    title: "Dispatch Control",
    purpose: "Protect the final customer moment: address, rider, timing, and payment collection.",
    action: "Verify address, rider, collection status, and delivery timing before dispatch.",
    mistake: "Do not leave balance collection or recipient details to memory.",
    successRule: "Every delivery leaves with confirmed route, recipient, and collection expectations.",
  },
  finance: {
    title: "Finance Discipline",
    purpose: "Keep collections, invoices, balances, and GST reporting trustworthy.",
    action: "Reconcile every payment and verify proof before marking balances clear.",
    mistake: "Do not treat verbal confirmation as payment proof.",
    successRule: "Every rupee should connect to an order, invoice, payment mode, and audit trail.",
  },
  marketing: {
    title: "Growth Intelligence",
    purpose: "Understand which channels, occasions, and products create high-quality demand.",
    action: "Review source performance, lost reasons, customer intent, and repeat-order patterns.",
    mistake: "Do not optimize campaigns on order count alone; quality and conversion matter.",
    successRule: "Marketing is working when it brings better-fit inquiries at lower operational friction.",
  },
  intelligence: {
    title: "Internal Insights",
    purpose: "Use internal signals to spot retention, revenue, staff, and operational patterns.",
    action: "Review insights as decision support, then assign clear human owners for action.",
    mistake: "Do not treat insights as customer-facing automation.",
    successRule: "Insight is valuable only when it changes an owned operational decision.",
  },
  calendar: {
    title: "Calendar Visibility",
    purpose: "See order density, delivery commitments, and date pressure at a glance.",
    action: "Check upcoming workload before promising dates or rush deliveries.",
    mistake: "Do not confirm a date without checking operational capacity.",
    successRule: "Every promised date should fit real production and dispatch capacity.",
  },
  admin: {
    title: "Control and Governance",
    purpose: "Manage users, rules, approvals, audit logs, and system health.",
    action: "Change settings carefully, review audit logs, and keep permissions clean.",
    mistake: "Do not change global rules without understanding team-wide impact.",
    successRule: "Admin changes should be intentional, reversible where possible, and auditable.",
  },
  profile: {
    title: "Your Staff Identity",
    purpose: "Keep your account secure and understand the role used for audit and permissions.",
    action: "Review your role and update your password when needed.",
    mistake: "Do not use another teammate's account to bypass permissions.",
    successRule: "Your account should represent your real operational responsibility.",
  },
};

export const tourSteps = [
  { pageKey: "dashboard", title: "Command Center", route: "/dashboard", body: "Start here each morning. Clear the urgent queues before looking at charts." },
  { pageKey: "follow-ups", title: "Follow-ups", route: "/follow-ups", body: "Work overdue customer actions first. Every inquiry needs a next step." },
  { pageKey: "whatsapp", title: "WhatsApp", route: "/whatsapp", body: "Review context and ownership. Inbound does not mean automatic reply." },
  { pageKey: "customers", title: "Customers", route: "/customers", body: "Use this as the customer memory layer for preferences and repeat orders." },
  { pageKey: "orders", title: "Orders", route: "/orders", body: "Confirm clean requirements before production gets the order." },
  { pageKey: "production", title: "Production", route: "/production", body: "Keep the board physically true and raise flags early." },
  { pageKey: "dispatch", title: "Dispatch", route: "/delivery", body: "Verify address, rider, timing, and collection before dispatch." },
  { pageKey: "finance", title: "Finance", route: "/accounts", body: "Reconcile payment proof, invoices, and pending balances." },
  { pageKey: "admin", title: "Control/Admin", route: "/admin", body: "Use carefully. These settings affect the whole Company OS." },
] as const;

export const pageKeyByPath = (path: string): GuidePageKey => {
  if (path.startsWith("/change-password")) return "change-password";
  if (path.startsWith("/initial-setup") || path.startsWith("/setup")) return "initial-setup";
  if (path.startsWith("/accept-invitation")) return "accept-invitation";
  if (path.startsWith("/follow-ups")) return "follow-ups";
  if (path.startsWith("/whatsapp")) return "whatsapp";
  if (path.startsWith("/customers")) return "customers";
  if (path.startsWith("/orders")) return "orders";
  if (path.startsWith("/production")) return "production";
  if (path.startsWith("/delivery")) return "dispatch";
  if (path.startsWith("/accounts")) return "finance";
  if (path.startsWith("/marketing")) return "marketing";
  if (path.startsWith("/intelligence")) return "intelligence";
  if (path.startsWith("/calendar")) return "calendar";
  if (path.startsWith("/admin")) return "admin";
  if (path.startsWith("/profile")) return "profile";
  if (path.startsWith("/login")) return "login";
  return "dashboard";
};
